import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import SearchResultGrid from "../searchResultGrid/searchResultGrid";
import Case from "../../interfaces/case";
import { caseColumnDef, caseColumnGroup } from "../../colDef/caseColumnDef";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import ToolbarContainer from "../baseComponents/toolbarContainer";
import FilterDrawer from "./filterDrawer";
import Filter from "../../interfaces/filters";
import {
  currentPoolMonth,
  debounce,
  decodedJWT,
  exportGridCasesToCSV,
  getOwnerType,
  handleSearchCase,
  isGridLeafColumn,
} from "../../utils/commonUtils";
import GridActions from "../gridActions/gridActions";
import JWTTokenPayload from "../../interfaces/JWTTokenPayload";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import RoleManagement from "../../security/roleManagement";
import { createFilters, getSelectedFiltersCount } from "../../utils/filterUtils";
import SeeDetailsDrawer from "../modals/seeDetailsDrawer";

const agencyFilters = [
  "interaction_type",
  "focused",
  "pin_code",
  "dpd_bucket",
  "client_name",
  "loan_type",
  "status",
  "l0_interaction",
  "segment_id",
  "state",
  "district",
  "principal_outstanding_amt",
  "total_due_amt"
];
const clientFilters = [
  "interaction_type",
  "focused",
  "client_manager_username",
  "pin_code",
  "dpd_bucket",
  "agency_name",
  "loan_type",
  "status",
  "l0_interaction",
  "state",
  "district",
  "principal_outstanding_amt",
  "total_due_amt"
];

const Dashboard: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const token: JWTTokenPayload | false = decodedJWT();
  const ownerType = getOwnerType(token);

  const segments = useSelector((state: RootState) => state.segments.segments);
  const clients = useSelector((state: RootState) => state.clients.clients);
  const agencies = useSelector((state: RootState) => state.agencies.agencies);
  const picklist = useSelector((state: RootState) => state.picklists.picklists);
  const districtAndStates = useSelector(
    (state: RootState) => state.districtsAndStates
  );
  const { gridSelectedCase } = useSelector((state: RootState) => state.gridSelectedCase);
  const states = districtAndStates.states;
  const districts = districtAndStates.districts.map((val) => val.district);
  const userSlice = useSelector((state: RootState) => state.userList);
  const users = userSlice.users;
  const clientManagerList = userSlice.clientManagerDescendants;

  const [filterResults, setFilterResults] = useState<Case[]>([]);
  const [selectedRows, setSelectedRows] = useState<Case[]>([]);
  const [leftMarginDashboard, setLeftMarginDashboard] =
    useState<string>("16px");
  const [loading, setLoading] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState<{
    page: number;
    pageSize: number;
  }>({ page: 0, pageSize: 40 });
  const [rowCount, setRowCount] = useState<number>(0);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<Filter[]>([]);
  const [universalSearchQuery, setUniversalSearchQuery] = useState<string>("");
  const [selectedPoolMonth, setSelectedPoolMonth] = useState<string>(
    currentPoolMonth()
  ); // Store month in parent
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [groupState, setGroupState] = React.useState<Record<string, { visible: boolean; button: boolean; }>>({
    'Case Data': { visible: true, button: false },
    'Latest Interaction': { visible: false, button: true },
    'Best Interaction': { visible: false, button: true },
    'Latest Call Interaction': { visible: false, button: true },
    'Best Call Interaction': { visible: false, button: true },
    'Latest Visit Interaction': { visible: false, button: true },
    'Best Visit Interaction': { visible: false, button: true },
    'Pincode Data': { visible: true, button: false },
    'Timestamps': { visible: true, button: false },
  });

  const visibleColumns = React.useMemo(() => {
    const result = [];
    for (const groupKey of caseColumnGroup) {
      const cols = groupKey.children.map(val => {
        if (isGridLeafColumn(val)) {
          return val.field;
        }
        return "";
      });
      result.push(...(groupState[groupKey.groupId].visible ? cols : [cols[0]]));
    }
	  const visibleColumnsSet: Set<string> = new Set(result);
    return caseColumnDef.filter((col) => visibleColumnsSet.has(col.field));
  }, [groupState]);

  const toggleGroup = (groupKey: string) => {
    setGroupState((prev) => ({
      ...prev,
      [groupKey]: { visible: !prev[groupKey].visible, button: prev[groupKey].button },
    }));
  };

  const clearResults = () => {
    setFilterResults([]);
    setRowCount(0);
  };

  const setDefaultFilters = () => {
    const finalFilters = createFilters(
      RoleManagement.isAgency(ownerType) ? agencyFilters : clientFilters,
      segments,
      clients,
      agencies,
      picklist,
      states,
      districts,
      users,
      clientManagerList
    );
    setFilters(finalFilters);
  };

  useEffect(() => {
    setDefaultFilters();
  }, [ownerType, ...(RoleManagement.isAgency(ownerType) ? [segments, clients] : [agencies, clientManagerList])]);

  const debouncedSearch = useMemo(
    () => debounce(handleSearchCase, 500),
    [paginationModel, selectedPoolMonth, universalSearchQuery, filters]
  );

  useEffect(() => {
    debouncedSearch.cancel(); // for safety don't in case the reference to the last function is lost and is not created again then this will be useful
    debouncedSearch(
      filters,
      universalSearchQuery,
      paginationModel.page,
      selectedPoolMonth,
      false,
      segments,
      "/api/v1/case",
      setLoading,
      clearResults,
      setRowCount,
      setFilterResults
    );
    return () => {
      debouncedSearch.cancel();
    };
  }, [paginationModel, universalSearchQuery, filters, selectedPoolMonth]);

  const handleSelectRow = (ids: GridRowSelectionModel) => {
    setSelectedRows(
      filterResults.filter((row) => Array.from(ids.ids).includes(row.case_id))
    );
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
    if (!drawerOpen) {
      setLeftMarginDashboard("166px");
    } else {
      setLeftMarginDashboard("16px");
    }
  };

  const resetFilters = () => {
    setDefaultFilters();
    setPaginationModel({ page: 0, pageSize: 40 });
  };

  const exportCSV = useCallback(
    () =>
      exportGridCasesToCSV(
        filters,
        universalSearchQuery,
        selectedPoolMonth,
        false,
        [],
        "/api/v1/case/download",
        setIsDownloading
      ),
    [filters, universalSearchQuery, selectedPoolMonth]
  );

  return (
    <>
      <Box sx={{ ml: leftMarginDashboard, mr: 2, fontSize: "12.5px" }}>
        <FilterDrawer
          filters={filters}
          setFilters={setFilters}
          drawerOpen={drawerOpen}
          toggleDrawer={toggleDrawer}
        />
        <ToolbarContainer
          setFilters={setFilters}
          toggleDrawer={toggleDrawer}
          filtersCount={getSelectedFiltersCount(filters)}
          universalSearchQuery={universalSearchQuery}
          setUniversalSearchQuery={setUniversalSearchQuery}
          selectedPoolMonth={selectedPoolMonth}
          setSelectedPoolMonth={setSelectedPoolMonth}
          resetFilters={resetFilters}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Button
            size="medium"
            sx={{ mr: 2 }}
            disabled={isDownloading}
            variant="outlined"
            color="primary"
            onClick={exportCSV}
          >
            {isDownloading ? (
              <CircularProgress color="primary" size={25} />
            ) : (
              <Typography variant="subtitle2">Export CSV</Typography>
            )}
          </Button>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <GridActions selectedCases={selectedRows} />
          </Box>
        </Box>
        {loading ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <SearchResultGrid
            height="70vh"
            disableFilter={true}
            disableSorting={true}
            disableAsecDescSorting={true}
            getRowId={(row) => row.case_id}
            results={filterResults}
            onSelectRow={handleSelectRow}
            columns={visibleColumns}
            rowCount={rowCount}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            columnGroupModel={caseColumnGroup}
            showToolBar={false}
            groupVisibilityMap={groupState}
            toggleGroupVisibility={toggleGroup}
          />
        )}
      </Box>
      <SeeDetailsDrawer />
    </>
  );
};

export default Dashboard;